/**
 * StorefrontPaymentMethods — Iter 191m.
 *
 * Sits above the existing Stripe PaymentElement on the storefront checkout.
 * Fetches the vendor's public payment-provider snapshot and renders a
 * method-selector tab bar. Each non-Stripe provider has its own render path:
 *
 *   • Stripe    — nothing to render here; the parent already renders <Elements/>
 *   • PayPal    — loads the PayPal Buttons SDK with the vendor's client_id
 *                 and calls our public create/capture endpoints.
 *   • Square    — loads the Square Web Payments SDK with the vendor's app_id
 *                 + location_id, tokenizes the card, sends the nonce to our
 *                 public /square/pay endpoint.
 *   • e-Transfer — no SDK; shows the vendor's Interac email + a generated
 *                  reference code, and calls our /etransfer/create-pending
 *                  endpoint so the order lands in the vendor's dashboard as
 *                  `awaiting_payment`.
 *
 * When the buyer completes any non-Stripe flow, we redirect back to the same
 * page with `?status=success&order_id=…` so the existing success screen fires.
 */
import React, { useCallback, useEffect, useRef, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Loader2, Check, CopyIcon, Mail, AlertCircle, CreditCard } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/* Dynamically loads a script tag once and resolves when ready. */
function loadScript(id, src) {
  return new Promise((resolve, reject) => {
    const existing = document.getElementById(id);
    if (existing) {
      if (existing.dataset.loaded === '1') return resolve(existing);
      existing.addEventListener('load', () => resolve(existing));
      existing.addEventListener('error', reject);
      return;
    }
    const s = document.createElement('script');
    s.id = id;
    s.src = src;
    s.async = true;
    s.onload = () => { s.dataset.loaded = '1'; resolve(s); };
    s.onerror = reject;
    document.head.appendChild(s);
  });
}

/* ─────────────────────────── PayPal Buttons ─────────────────────────── */
function PayPalButtons({ subdomain, amount, currency, onPaid }) {
  const containerRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        // Fetch vendor's public PayPal client_id from our snapshot endpoint
        const { data } = await axios.get(`${API}/builder/payment-providers/public/${subdomain}`);
        const client = data?.paypal?.client_id;
        if (!client) throw new Error('PayPal not configured for this store');
        const sdkUrl = `https://www.paypal.com/sdk/js?client-id=${encodeURIComponent(client)}&currency=${encodeURIComponent(currency)}&enable-funding=venmo,paylater&components=buttons`;
        await loadScript('paypal-sdk', sdkUrl);
        if (cancelled) return;
        setLoading(false);

        window.paypal.Buttons({
          style: { layout: 'vertical', shape: 'pill', color: 'blue', label: 'paypal' },
          createOrder: async () => {
            try {
              const { data: order } = await axios.post(`${API}/builder/paypal/public/${subdomain}/orders`, {
                amount: amount.toFixed(2),
                currency,
              });
              return order.id;
            } catch (e) {
              toast.error(e.response?.data?.detail || 'PayPal order failed');
              throw e;
            }
          },
          onApprove: async (data) => {
            try {
              const { data: capture } = await axios.post(`${API}/builder/paypal/public/${subdomain}/orders/${data.orderID}/capture`);
              if (capture.status === 'COMPLETED') {
                toast.success('Payment received via PayPal');
                onPaid?.(data.orderID);
              } else {
                toast.error('PayPal payment not completed');
              }
            } catch (e) {
              toast.error(e.response?.data?.detail || 'PayPal capture failed');
            }
          },
          onError: (e) => {
            toast.error('PayPal error — try a different method');
            console.error(e);
          },
        }).render(containerRef.current);
      } catch (e) {
        if (!cancelled) { setErr(String(e?.message || e)); setLoading(false); }
      }
    })();
    return () => { cancelled = true; };
  }, [subdomain, amount, currency, onPaid]);

  return (
    <div data-testid="paypal-buttons">
      {loading && <div className="py-6 flex items-center gap-2 text-white/60"><Loader2 size={14} className="animate-spin" /> Loading PayPal…</div>}
      {err && <div className="p-3 rounded-lg bg-red-500/10 border border-red-400/30 text-red-200 text-xs">{err}</div>}
      <div ref={containerRef} />
      <p className="mt-3 text-[10px] text-white/45 tracking-wide">
        Includes PayPal, Venmo, and Pay Later when supported by your account.
      </p>
    </div>
  );
}

/* ─────────────────────────── Square card ─────────────────────────── */
function SquareCard({ subdomain, amount, currency, onPaid }) {
  const cardRef = useRef(null);
  const cardObjRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [err, setErr] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await axios.get(`${API}/builder/payment-providers/public/${subdomain}`);
        const sq = data?.square;
        if (!sq?.application_id || !sq?.location_id) throw new Error('Square not configured');
        const sdk = sq.sandbox
          ? 'https://sandbox.web.squarecdn.com/v1/square.js'
          : 'https://web.squarecdn.com/v1/square.js';
        await loadScript('square-sdk', sdk);
        if (cancelled) return;
        const payments = window.Square.payments(sq.application_id, sq.location_id);
        const card = await payments.card();
        await card.attach(cardRef.current);
        cardObjRef.current = card;
        setLoading(false);
      } catch (e) { if (!cancelled) { setErr(String(e?.message || e)); setLoading(false); } }
    })();
    return () => { cancelled = true; };
  }, [subdomain]);

  const pay = useCallback(async () => {
    if (!cardObjRef.current) return;
    setProcessing(true); setErr('');
    try {
      const result = await cardObjRef.current.tokenize();
      if (result.status !== 'OK') throw new Error(result.errors?.[0]?.message || 'Card tokenization failed');
      const { data } = await axios.post(`${API}/builder/square/public/${subdomain}/payments`, {
        source_id: result.token,
        amount: amount.toFixed(2),
        currency,
      });
      if (data?.payment?.status === 'COMPLETED' || data?.payment?.status === 'APPROVED') {
        toast.success('Payment received via Square');
        onPaid?.(data.payment.id);
      } else {
        setErr(`Square status: ${data?.payment?.status || 'unknown'}`);
      }
    } catch (e) { setErr(e.response?.data?.detail || String(e?.message || e)); }
    finally { setProcessing(false); }
  }, [subdomain, amount, currency, onPaid]);

  return (
    <div data-testid="square-card">
      {loading && <div className="py-6 flex items-center gap-2 text-white/60"><Loader2 size={14} className="animate-spin" /> Loading Square…</div>}
      {err && <div className="p-3 rounded-lg bg-red-500/10 border border-red-400/30 text-red-200 text-xs mb-3">{err}</div>}
      <div ref={cardRef} className="rounded-lg overflow-hidden bg-white p-2 min-h-[50px]" />
      {!loading && (
        <button
          type="button" onClick={pay} disabled={processing}
          className="mt-4 w-full py-4 rounded-full text-sm font-bold uppercase tracking-[0.18em] text-white disabled:opacity-40 inline-flex items-center justify-center gap-2"
          style={{ background: 'linear-gradient(135deg, #000 0%, #333 100%)' }}
          data-testid="square-pay-btn"
        >
          <CreditCard size={13} /> {processing ? 'Processing…' : `Pay $${amount.toFixed(2)} with Square`}
        </button>
      )}
    </div>
  );
}

/* ─────────────────────────── e-Transfer ─────────────────────────── */
function ETransferPanel({ subdomain, amount, currency, customerEmail, items, onPaid }) {
  const [order, setOrder] = useState(null);
  const [creating, setCreating] = useState(false);
  const [err, setErr] = useState('');

  const start = useCallback(async () => {
    setCreating(true); setErr('');
    try {
      const { data } = await axios.post(`${API}/builder/etransfer/public/${subdomain}/create-pending`, {
        customer_email: customerEmail,
        amount,
        items: items || [],
      });
      setOrder(data);
    } catch (e) { setErr(e.response?.data?.detail || 'Could not create order'); }
    finally { setCreating(false); }
  }, [subdomain, amount, customerEmail, items]);

  const copy = (v) => { navigator.clipboard?.writeText(v); toast.success('Copied'); };

  if (!order) {
    return (
      <div data-testid="etransfer-panel" className="rounded-2xl bg-white/[0.02] border border-white/10 p-6">
        <div className="flex items-start gap-3">
          <Mail className="w-6 h-6 text-emerald-300 shrink-0 mt-0.5" />
          <div>
            <h4 className="text-white font-semibold">Pay with Interac e-Transfer</h4>
            <p className="mt-1 text-sm text-white/60">
              Click below to reveal the store&apos;s Interac auto-deposit email + a unique reference code. Send the e-Transfer
              from your online banking; the store will mark your order as paid on receipt.
            </p>
            {err && <div className="mt-3 p-2 rounded bg-red-500/10 border border-red-400/30 text-red-200 text-xs">{err}</div>}
            <button
              onClick={start} disabled={creating || !customerEmail}
              className="mt-4 px-5 py-3 rounded-full text-xs font-bold uppercase tracking-[0.18em] text-[#0E0E23] bg-emerald-300 hover:bg-emerald-200 disabled:opacity-40 inline-flex items-center gap-2"
              data-testid="etransfer-start-btn"
            >
              {creating ? <Loader2 size={12} className="animate-spin" /> : <Check size={12} />}
              Show payment instructions
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="etransfer-instructions" className="rounded-2xl bg-emerald-500/[0.06] border border-emerald-400/30 p-6">
      <div className="flex items-center gap-2 text-emerald-300 text-[10px] uppercase tracking-[0.24em] font-bold mb-3">
        <Mail size={12} /> e-Transfer instructions
      </div>
      <div className="space-y-3 text-sm text-white/85">
        <Row label="Send to" value={order.recipient_email} onCopy={() => copy(order.recipient_email)} testid="etr-email" />
        <Row label="Amount" value={`$${order.amount.toFixed(2)} ${order.currency}`} />
        <Row label="Message / memo" value={order.reference_code} onCopy={() => copy(order.reference_code)} testid="etr-ref" hint="Include this exactly so we can match your payment." />
        {!order.auto_deposit && (
          <>
            <Row label="Security question" value={order.security_question} />
            <div className="p-2 rounded bg-amber-500/10 border border-amber-400/30 text-amber-200 text-[11px]">
              <AlertCircle size={11} className="inline mr-1" /> The store will contact you for the security answer if their auto-deposit isn&apos;t set.
            </div>
          </>
        )}
      </div>
      <button
        onClick={() => onPaid?.(order.order_id, { pending: true })}
        className="mt-5 w-full py-3.5 rounded-full text-xs font-bold uppercase tracking-[0.18em] text-white"
        style={{ background: 'linear-gradient(135deg, #10B981, #059669)' }}
        data-testid="etransfer-sent-btn"
      >
        I&apos;ve sent the e-Transfer
      </button>
      <p className="mt-3 text-[10px] text-white/45 text-center">
        The store will confirm receipt within one business day. You&apos;ll get an email once it&apos;s marked paid.
      </p>
    </div>
  );
}

function Row({ label, value, onCopy, hint, testid }) {
  return (
    <div>
      <div className="flex items-center justify-between gap-3">
        <span className="text-[11px] uppercase tracking-wider text-white/45 font-bold">{label}</span>
        {onCopy && (
          <button onClick={onCopy} data-testid={testid} className="text-[10px] font-bold uppercase tracking-wider text-white/70 hover:text-white inline-flex items-center gap-1">
            <CopyIcon size={10} /> Copy
          </button>
        )}
      </div>
      <div className="mt-1 rounded-lg bg-[#0A0A1F] border border-white/10 px-3 py-2 font-mono text-[13px] text-white break-all">{value}</div>
      {hint && <p className="mt-1 text-[11px] text-white/45">{hint}</p>}
    </div>
  );
}

/* ─────────────────────────── Container ─────────────────────────── */
export default function StorefrontPaymentMethods({
  subdomain, amount, currency = 'USD', customerEmail, items,
  method, onMethodChange, renderStripe, onPaid,
}) {
  const [providers, setProviders] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/builder/payment-providers/public/${subdomain}`);
        setProviders(data || {});
      } catch { setProviders({}); }
    })();
  }, [subdomain]);

  if (providers === null) {
    return <div className="py-6 flex items-center gap-2 text-white/50"><Loader2 size={14} className="animate-spin" /> Loading payment methods…</div>;
  }

  const tabs = [{ id: 'stripe', label: 'Card', hint: 'Apple / Google Pay included' }];
  if (providers.paypal)    tabs.push({ id: 'paypal',    label: 'PayPal',       hint: 'Also Venmo' });
  if (providers.square)    tabs.push({ id: 'square',    label: 'Square',       hint: 'Card via Square' });
  if (providers.etransfer) tabs.push({ id: 'etransfer', label: 'e-Transfer',   hint: 'Canada · No fees' });

  const active = tabs.find(t => t.id === method) ? method : 'stripe';

  return (
    <div data-testid="payment-methods">
      {tabs.length > 1 && (
        <div className="flex gap-1 mb-4 rounded-xl bg-white/[0.03] border border-white/10 p-1" data-testid="payment-method-tabs">
          {tabs.map((t) => (
            <button
              key={t.id}
              type="button"
              onClick={() => onMethodChange(t.id)}
              className={`flex-1 px-3 py-2 rounded-lg text-[11px] font-bold uppercase tracking-wider transition-all ${
                active === t.id
                  ? 'bg-gradient-to-r from-[#8B5CF6] to-[#A78BFA] text-white shadow'
                  : 'text-white/60 hover:text-white hover:bg-white/[0.04]'
              }`}
              data-testid={`payment-method-${t.id}`}
            >
              {t.label}
              <span className="block text-[9px] font-normal opacity-70 mt-0.5">{t.hint}</span>
            </button>
          ))}
        </div>
      )}

      {active === 'stripe'   && renderStripe()}
      {active === 'paypal'   && <PayPalButtons subdomain={subdomain} amount={amount} currency={currency} onPaid={onPaid} />}
      {active === 'square'   && <SquareCard subdomain={subdomain} amount={amount} currency={currency} onPaid={onPaid} />}
      {active === 'etransfer' && <ETransferPanel subdomain={subdomain} amount={amount} currency="CAD" customerEmail={customerEmail} items={items} onPaid={onPaid} />}
    </div>
  );
}
